import Vue from "vue";
import DOMPurify from "dompurify";
import lodashGet from "lodash/get";
import MobileDetect from "mobile-detect";

const cuilGen = function (numeroDocumento, sexo) {
  /**
   * Cuil format is: AB - document_number - C
   * Author: Nahuel Sanchez, Woile
   *
   * @param {str} document_number -> string solo digitos
   * @param {str} gender -> debe contener HOMBRE, MUJER o SOCIEDAD
   *
   * @return {str}
   **/
  let HOMBRE = ["HOMBRE", "M", "MALE"];
  let MUJER = ["MUJER", "F", "FEMALE"];
  let SOCIEDAD = ["SOCIEDAD", "S", "SOCIETY"];
  let AB, C;

  /**
   * Verifico que el document_number tenga exactamente ocho numeros y que
   * la cadena no contenga letras.
   */
  if (
    numeroDocumento.length < 7 ||
    numeroDocumento.length > 8 ||
    isNaN(numeroDocumento)
  ) {
    // Muestro un error en caso de no serlo.
    Vue.$log.error("El numero de documento ingresado no es correcto.");
    return "";
  }

  if (numeroDocumento.length === 7) {
    numeroDocumento = "0".concat(numeroDocumento);
  }

  /**
   * De esta manera permitimos que el gender venga en minusculas,
   * mayusculas y titulo.
   */
  sexo = sexo.toUpperCase();

  // Defino el valor del prefijo.
  if (HOMBRE.indexOf(sexo) >= 0) {
    AB = "20";
  } else if (MUJER.indexOf(sexo) >= 0) {
    AB = "27";
  } else {
    AB = "30";
  }

  /*
   * Los numeros (excepto los dos primeros) que le tengo que
   * multiplicar a la cadena formada por el prefijo y por el
   * numero de document_number los tengo almacenados en un arreglo.
   */
  let multiplicadores = [3, 2, 7, 6, 5, 4, 3, 2];

  // Realizo las dos primeras multiplicaciones por separado.
  let calculo = parseInt(AB.charAt(0)) * 5 + parseInt(AB.charAt(1)) * 4;

  /*
   * Recorro el arreglo y el numero de document_number para
   * realizar las multiplicaciones.
   */
  for (let i = 0; i < 8; i++) {
    calculo += parseInt(numeroDocumento.charAt(i)) * multiplicadores[i];
  }

  // Calculo el resto.
  let resto = parseInt(calculo) % 11;

  /*
   * Llevo a cabo la evaluacion de las tres condiciones para
   * determinar el valor de C y conocer el valor definitivo de
   * AB.
   */
  if (SOCIEDAD.indexOf(sexo) < 0 && resto === 1) {
    C = HOMBRE.indexOf(sexo) >= 0 ? "9" : "4";
    AB = "23";
  } else if (resto === 0) {
    C = "0";
  } else {
    C = 11 - resto;
  }

  // Generate cuit
  return [AB, numeroDocumento, C].join("");
};

const replaceAll = function (str, search, replacement) {
  return str.split(search).join(replacement);
};

const getParameterByName = (name, url) => {
  if (!url) url = window.location.href;
  let results = new RegExp(
    "[?&]" + name.replace(/[[\]]/g, "\\$&") + "(=([^&#]*)|&|#|$)"
  ).exec(url);

  if (!results) return null;
  if (!results[2]) return "";
  return decodeURIComponent(results[2].replace(/\+/g, " "));
};

const getTrackingCodeLocal = name => {
  let tc = getParameterByName(name, window.location.href);

  if (tc != null && tc !== "") {
    return tc;
  }
  return null;
};

const obtenerTrackingCode = function () {
  let keys = ["cid", "onsite", "ema", "psm", "dis", "afi", "afl"];
  let tc = "";

  for (let key of keys) {
    let tccan = getTrackingCodeLocal(key);
    if (tccan !== null) {
      tc = tccan;
      break;
    }
  }

  if (tc === "") {
    return "";
  }

  let tcSplit = "";
  let tcBuild = "";

  if (tc.indexOf(":") === -1) {
    // Tracking codes viejos
    tcSplit = tc.split("-");
  } else {
    // Tracking codes nuevos
    replaceAll(tc, "_", " ");
    tcSplit = tc.split("-")[0].split(":");
  }

  tcBuild =
    tcSplit[0].trim() +
    "-" +
    tcSplit[1].trim() +
    "-" +
    tcSplit[2].trim() +
    "-" +
    tcSplit[3].trim();

  return "-" + tcBuild;
};

const getEnvironment = () => {
  const currentHostname = window.location.hostname;
  const prodHosts = ["www.bbva.com.ar", "bbva.com.ar", "apps.bbva.com.ar"];
  const testHosts = ["app-work.bbva.com.ar"];

  // Check if the current hostname matches any of the known hostnames
  if (prodHosts.includes(currentHostname)) {
    return {
      name: "prod",
      description: "Produccion",
      adobeLaunch:
        "//assets.adobedtm.com/95c3e405673d/81c80ecf6aaf/launch-144c155dcd64.min.js"
    };
  } else if (testHosts.includes(currentHostname)) {
    return {
      name: "qa",
      description: "Test",
      adobeLaunch:
        "//assets.adobedtm.com/95c3e405673d/81c80ecf6aaf/launch-8bd807155300-development.min.js"
    };
  }

  // No identificado? usar development
  return {
    name: "de",
    description: "Desarrollo",
    adobeLaunch:
      "//assets.adobedtm.com/95c3e405673d/81c80ecf6aaf/launch-8bd807155300-development.min.js"
  };
};

const toTitleCase = function (str) {
  if (!!str && str.trim() !== "") {
    return str.replace(
      /\w\S*/g,
      txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
  }
  return str;
};

const isDev = function () {
  return !PRODUCTION;
};

/* eslint-disable no-useless-escape */
/**
 * Verificar por el userAgent si el dispositivo es
 * mobile o no
 */
const _isMobile = () => {
  const md = new MobileDetect(window.navigator.userAgent);
  return md.mobile() !== null;
};

const isValidDate = function (dateString) {
  // Verificar patrón
  let regex_date = /\d{2}\/\d{2}\/\d{4}/;

  if (!regex_date.test(dateString)) {
    return false;
  }

  // Parsear string de fecha
  const parts = dateString.split("/");
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);

  // Rango de meses y años
  if (year < 1000 || year > 3000 || month == 0 || month > 12) {
    return false;
  }

  const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  // Ajustar años biciestos
  /*istanbul ignore else*/
  if (year % 400 == 0 || (year % 100 != 0 && year % 4 == 0)) {
    monthLength[1] = 29;
  }

  // Verificar rango de días
  return day > 0 && day <= monthLength[month - 1];
};

const copyToClipboard = async function (text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      // Falló la copia del texto
      return false;
    }
  }

  // Api de clipiboard no disponible
  return false;
};

/**
 * Función de orden superior de lodash.get
 * que convierte null a undefined
 */
const get = (o, path, defval) => {
  return lodashGet(o, path, defval) === null
    ? defval
    : lodashGet(o, path, defval);
};

const cleanHtmlTags = html => {
  const miDiv = document.createElement("div");
  miDiv.innerHTML = DOMPurify.sanitize(html);
  return miDiv.innerText || "";
};

const worldCupInternalLink = cardTitle => {
  const offers = {
    Referidos: "referir",
    Seguros: "contratar-seguros",
    Jubilados: "conocer mas-jubilados",
    Emprendedores: "conocer mas-emprendedores",
    "Fondos, bonos y acciones": "invertir",
    "Tarjeta de crédito": "contratar-paquetes",
    "Cuenta sueldo": "conocer mas-cuenta sueldo",
    "Consumos con tu tarjeta": "conocer mas-tarjetas"
  };

  return offers[cardTitle] || "";
};
const normalizeCharacters = str => {
  /** 2021-07-06: Merlin no normaliza las ENIES vienen como ? */
  return str
    ? (str + "")
        .replace(/[?=]/g, "N")
        .replace(/[#=]/g, "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
    : str;
};

const tipoDocumentoPatchNormativa = (tipoDoc, numeroDoc, sexo) => {
  // Solo tipo 27 = Hombre, 28 = Mujer si el DNI es menor a 10000000 y no es extranjero
  if (tipoDoc.trim() !== "31" && parseInt(numeroDoc.trim()) < 10000000) {
    if (sexo === "M") {
      return "27";
    } else {
      return "28";
    }
  }
  return tipoDoc;
};

export {
  get,
  isDev,
  cuilGen,
  _isMobile,
  replaceAll,
  isValidDate,
  getEnvironment,
  copyToClipboard,
  toTitleCase,
  getParameterByName,
  obtenerTrackingCode,
  getTrackingCodeLocal,
  cleanHtmlTags,
  worldCupInternalLink,
  normalizeCharacters,
  tipoDocumentoPatchNormativa
};
